import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../API';
import styles from './ProtocolSelectionModal.module.css';

/**
 * Create or rename a sectioning protocol (name + description only).
 * Used from Block Setup — not Case Builder load.
 *
 * editProtocolId: when set, edit that protocol; otherwise create.
 * createOnly: legacy alias — ignored when editProtocolId is set; create form always shown for create.
 */
function ProtocolSelectionModal({
    isOpen,
    onClose,
    onSelect,
    editProtocolId = null,
    createOnly: _createOnly = false, // call-site compat (Block Setup); form is always create or edit
}) {
    const queryClient = useQueryClient();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');

    const isEditMode = !!editProtocolId;

    const { data: protocolsResponse } = useQuery({
        queryKey: ['bdsa-protocol'],
        queryFn: () => api.get('/bdsa/protocol'),
        enabled: isOpen && isEditMode,
        staleTime: 5 * 60 * 1000,
    });

    const protocols = protocolsResponse?.status === 200 && Array.isArray(protocolsResponse.data)
        ? protocolsResponse.data
        : [];
    const protocolToEdit = protocols.find((p) => p._id === editProtocolId);

    useEffect(() => {
        if (!isOpen) return;
        if (isEditMode && protocolToEdit) {
            setName(protocolToEdit.name || '');
            setDescription(protocolToEdit.description || '');
        } else if (!isEditMode) {
            setName('');
            setDescription('');
        }
    }, [isOpen, isEditMode, protocolToEdit?._id, protocolToEdit?.name, protocolToEdit?.description]);

    const createProtocolMutation = useMutation({
        mutationFn: async (protocolData) => {
            const response = await api.post('/bdsa/protocol', protocolData);
            if (response.status !== 200) {
                throw new Error('Failed to create protocol');
            }
            return response;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries(['bdsa-protocol']);
            if (data?.data && onSelect) {
                onSelect(data.data);
            }
            onClose();
            setName('');
            setDescription('');
        },
    });

    const updateProtocolMutation = useMutation({
        mutationFn: async (protocolData) => {
            const response = await api.put(`/bdsa/protocol/${editProtocolId}`, {
                body: protocolData.body,
            });
            if (response.status !== 200) {
                throw new Error('Failed to update protocol');
            }
            return response;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries(['bdsa-protocol']);
            if (data.data) {
                onSelect?.(data.data);
            }
            onClose();
            setName('');
            setDescription('');
        },
    });

    if (!isOpen) return null;

    const handleSave = () => {
        if (!name.trim()) return;
        if (isEditMode) {
            updateProtocolMutation.mutate({
                body: {
                    name: name.trim(),
                    description: description.trim() || '',
                },
            });
        } else {
            createProtocolMutation.mutate({
                query: { name: name.trim() },
                body: { description: description.trim() || '' },
            });
        }
    };

    const pending = createProtocolMutation.isPending || updateProtocolMutation.isPending;

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h3 className={styles.modalTitle}>
                        {isEditMode ? 'Edit Sectioning Protocol' : 'Create Sectioning Protocol'}
                    </h3>
                    <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
                        ×
                    </button>
                </div>

                <div className={styles.modalBody}>
                    <div className={styles.createForm}>
                        <div className={styles.formField}>
                            <label htmlFor="protocol-name">Protocol Name *</label>
                            <input
                                id="protocol-name"
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="e.g., UPMC ADRC Sections"
                            />
                        </div>
                        <div className={styles.formField}>
                            <label htmlFor="protocol-description">Description (optional)</label>
                            <textarea
                                id="protocol-description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="e.g., Standard sampling for an ADRC brain workup at UPMC."
                                rows={3}
                            />
                        </div>
                        <div className={styles.formActions}>
                            <button
                                type="button"
                                className={styles.createButton}
                                onClick={handleSave}
                                disabled={!name.trim() || pending}
                            >
                                {isEditMode
                                    ? (pending ? 'Updating…' : 'Update Protocol')
                                    : (pending ? 'Creating…' : 'Create Protocol')}
                            </button>
                            <button type="button" className={styles.cancelButton} onClick={onClose}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProtocolSelectionModal;
